import {flatMap} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled, {css} from 'styled-components';

import {
  OnboardingEventsEnum,
  trackEvent,
  TrackingTypesEnum,
} from '../../../libs/shared/core/src/services/tracking/tracking';
import {CenteredPopover} from '../../../libs/shared/ui/src/components/popovers/centeredPopover';
import {PopoverProps} from '../../../libs/shared/ui/src/components/popovers/popoverTypes';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {
  newOnboardingSlideHeight,
  newOnboardingSlideWidth,
} from '../onboarding/welcome/onboardingWelcomeHelpers';
import {addSlideGridContainerStyle, CarouselSlideDefinition, CarouselSlideFooterLeft} from './carouselCommon';
import {CarouselSlide} from './carouselSlide';

/*
 * Props.
 */

export interface CarouselProps extends PopoverProps {
  slides: ReadonlyArray<CarouselSlideDefinition>;
  onRequestClose: () => void;
}

/*
 * Style.
 */

interface StyledPropsWithSlide {
  $currentSlide: number;
}

const StyledCarouselDiv = styled.div<StyledPropsWithSlide>`
  ${(p) => addSlideDimensions(p.$currentSlide)}
  overflow: hidden;

  ${addSlideGridContainerStyle()}

  border-radius: 24px;
  background-color: ${(p) => p.theme.foreground};
  ${(p) => boxShadow(p.theme, `0 25px 65px 0 ${p.theme.alphas.black30}`)};
  position: relative;
`;

function addSlideDimensions($currentSlide: number) {
  if ($currentSlide === 0) {
    return css`
      width: ${newOnboardingSlideWidth};
      height: ${newOnboardingSlideHeight};
    `;
  }
  return css`
    width: 800px;
    height: 451px;
  `;
}

const StyledCarouselTextSpan = styled.span<StyledPropsWithSlide>`
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};

  ${(p) =>
    p.$currentSlide === 0 &&
    css`
      margin-top: 20px;
    `}
`;

/*
 * Component.
 */

export const Carousel: FC<CarouselProps> = (props) => {
  const {slides, onRequestClose} = props;
  const slidesCount = slides.length;
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  /*
   * Event handlers.
   */

  const onPreviousClick = useCallback(() => {
    trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.PREVIOUS);

    setCurrentSlideIndex((value) => (value === 0 ? value : value - 1));
  }, []);

  const onNextClick = useCallback(
    (isSkip?: boolean) => {
      // If it's the last slide, clicking Next should close the carousel.
      if (currentSlideIndex === slidesCount - 1) {
        onRequestClose();
      } else {
        if (!isSkip) {
          trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.NEXT);
        }
        setCurrentSlideIndex((value) => (value >= slidesCount - 1 ? value : value + 1));
      }
    },
    [currentSlideIndex, slidesCount, onRequestClose],
  );

  /*
   * Lifecycle.
   */

  // Preload the images for the slides on load.
  useEffect(() => {
    preloadSlideImagesAsync(slides);
  }, [slides]);

  // Current slide.
  const slideDefinition = slides[currentSlideIndex];

  /*
   * Render.
   */

  return (
    <CenteredPopover isExclusive>
      <StyledCarouselDiv $currentSlide={currentSlideIndex}>
        <CarouselSlide
          index={currentSlideIndex}
          slide={slideDefinition}
          onNextClick={onNextClick}
          onPreviousClick={onPreviousClick}
          totalStepCount={slidesCount}
        />
        {/* Only show the carousel slide footer left section for non-video slides.
        This is because the video slide has its own left footer that gets rendered. */}
        {currentSlideIndex !== 0 ? (
          <CarouselSlideFooterLeft>
            <StyledCarouselTextSpan $currentSlide={currentSlideIndex}>{`${
              currentSlideIndex + 1
            } of ${slidesCount}`}</StyledCarouselTextSpan>
          </CarouselSlideFooterLeft>
        ) : null}
      </StyledCarouselDiv>
    </CenteredPopover>
  );
};

/*
 * Helpers.
 */

async function preloadSlideImagesAsync(slides: ReadonlyArray<CarouselSlideDefinition>) {
  const imageUrls = flatMap(slides, (slide) => slide.imageUrls || []);

  // Preload them in sequence.
  for (const imageUrl of imageUrls) // eslint-disable-line
    await preloadImageAsync(imageUrl); // eslint-disable-line
}

function preloadImageAsync(imageUrl: string) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(undefined);
    image.onerror = () => resolve(undefined);
    image.src = imageUrl;
  });
}
